/**
 * Pagina /superadmin/categorias — Sprint 13 S13-23.
 *
 * Mantenedor de categorias del catalogo financiero: tipos de ingreso
 * y gasto que el tesorero usa al clasificar pagos, registrar gastos,
 * ingresos varios, compensaciones. Cada categoria tiene un color
 * para uso en graficos del portal.
 *
 * DELETE es soft: marca activa=false. La fila persiste para no
 * romper la trazabilidad de registros historicos que la referencian
 * (gastos, compensaciones, etc.). El filtro "Solo activas" oculta
 * las desactivadas.
 *
 * RBAC ruta: super_admin (sa). Backend acepta tambien tesorero, pero
 * la UI vive solo en sa.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarCategoria,
  crearCategoria,
  desactivarCategoria,
  listCategorias,
  type CategoriaCreate,
} from '../../lib/api/categorias';
import type { CategoriaRead, TipoCategoria } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';

const ETIQUETA_TIPO: Record<TipoCategoria, string> = {
  ingreso: 'Ingreso',
  gasto: 'Gasto',
};

const COLORES_SUGERIDOS = [
  '#22c55e',
  '#ef4444',
  '#3b82f6',
  '#f59e0b',
  '#a855f7',
  '#14b8a6',
  '#ec4899',
  '#6b7280',
];

interface ModalFormProps {
  inicial: CategoriaRead | null;
  candidatosPadre: CategoriaRead[];
  onCerrar: () => void;
  onGuardada: () => void;
}

function ModalForm({
  inicial,
  candidatosPadre,
  onCerrar,
  onGuardada,
}: ModalFormProps) {
  const esEdicion = inicial !== null;
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [tipo, setTipo] = useState<TipoCategoria>(inicial?.tipo ?? 'gasto');
  const [color, setColor] = useState(inicial?.color ?? '#3b82f6');
  const [padreId, setPadreId] = useState<string>(
    inicial?.categoria_padre_id ?? '',
  );
  const [esFondoTerceros, setEsFondoTerceros] = useState(
    inicial?.es_fondo_terceros ?? false,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = nombre.trim().length > 0 && /^#[0-9a-fA-F]{6}$/.test(color);

  // Padres validos: solo raices (sin padre) del mismo tipo, y excluyendo
  // la categoria actual (en edicion). Los hijos no pueden ser padres.
  const padresFiltrados = candidatosPadre.filter(
    (c) =>
      c.tipo === tipo &&
      c.categoria_padre_id === null &&
      c.id !== inicial?.id,
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: CategoriaCreate = {
        nombre: nombre.trim(),
        tipo,
        color,
        es_fondo_terceros: esFondoTerceros,
        categoria_padre_id: padreId || null,
      };
      if (esEdicion) {
        await actualizarCategoria(inicial.id, payload);
      } else {
        await crearCategoria(payload);
      }
      onGuardada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={esEdicion ? 'Editar categoría' : 'Nueva categoría'}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            {esEdicion ? 'Editar categoría' : 'Nueva categoría'}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Nombre <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Jardinería, Telecomunicaciones, Multa…"
            maxLength={100}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Tipo <span className="text-state-danger">*</span>
          </span>
          <select
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as TipoCategoria);
              // Reset del padre si cambia el tipo (padre debe ser del mismo tipo).
              setPadreId('');
            }}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            {(['ingreso', 'gasto'] as TipoCategoria[]).map((t) => (
              <option key={t} value={t} className="bg-bg-sunken text-text-primary">
                {ETIQUETA_TIPO[t]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Categoría padre{' '}
            <span className="text-text-muted">(opcional)</span>
          </span>
          <select
            value={padreId}
            onChange={(e) => setPadreId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            <option value="" className="bg-bg-sunken text-text-primary">
              — Sin padre (categoría raíz) —
            </option>
            {padresFiltrados.map((p) => (
              <option key={p.id} value={p.id} className="bg-bg-sunken text-text-primary">
                {p.nombre}
              </option>
            ))}
          </select>
          {padresFiltrados.length === 0 ? (
            <span className="text-xs text-state-warning block mt-1">
              ⚠ No hay categorías raíz de tipo "{ETIQUETA_TIPO[tipo].toLowerCase()}"
              que puedan ser padre. Crea primero una categoría raíz del mismo
              tipo para poder anidar.
            </span>
          ) : (
            <span className="text-xs text-text-muted block mt-1">
              Solo se pueden anidar 2 niveles. Si una categoría ya es hija, no
              puede ser padre de otra.
            </span>
          )}
        </label>

        <div className="text-sm space-y-1">
          <span className="text-text-secondary block">
            Color <span className="text-state-danger">*</span>
          </span>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-16 rounded cursor-pointer bg-bg-sunken border-[0.5px] border-white/15"
              aria-label="Selector de color"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              pattern="^#[0-9a-fA-F]{6}$"
              maxLength={7}
              className="flex-1 bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary font-mono"
            />
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {COLORES_SUGERIDOS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className="w-6 h-6 rounded-sm border-[0.5px] border-white/15"
                style={{ backgroundColor: c }}
                aria-label={`Usar color ${c}`}
              />
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={esFondoTerceros}
            onChange={(e) => setEsFondoTerceros(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="text-text-primary">Fondos de terceros</span>
            <span className="block text-xs text-text-muted mt-0.5">
              Para campañas en que el barrio recauda y luego transfiere a un
              tercero (muni, JJVV). Los movimientos tocan el banco pero NO
              cuentan como recaudación/gastos del barrio; se trazan en el
              reporte de fondos de terceros.
            </span>
          </span>
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear categoría'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function CategoriasPage() {
  const [items, setItems] = useState<CategoriaRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<TipoCategoria | 'todos'>('todos');
  const [soloActivas, setSoloActivas] = useState(true);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [editando, setEditando] = useState<CategoriaRead | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [desactivandoId, setDesactivandoId] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listCategorias({})
      .then((data) => {
        if (cancelado) return;
        setItems(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar categorías`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  const filtradas = useMemo(
    () =>
      items.filter((c) => {
        if (filtroTipo !== 'todos' && c.tipo !== filtroTipo) return false;
        if (soloActivas && !c.activa) return false;
        return true;
      }),
    [items, filtroTipo, soloActivas],
  );

  // Sprint 13 S13-30: ordena las categorias para renderizar padres con
  // sus hijos justo debajo. Las raices van primero (alfabeticas por
  // tipo+nombre); los hijos siguen al padre, tambien alfabeticos.
  // Cualquier hijo "huerfano" (cuyo padre no esta en la lista filtrada)
  // se trata como raiz para que no se pierda.
  const ordenadas = useMemo(() => {
    const result: CategoriaRead[] = [];
    const porPadre: Record<string, CategoriaRead[]> = {};
    for (const c of filtradas) {
      if (c.categoria_padre_id) {
        (porPadre[c.categoria_padre_id] ??= []).push(c);
      }
    }
    const raices = filtradas
      .filter(
        (c) =>
          c.categoria_padre_id === null ||
          !filtradas.find((x) => x.id === c.categoria_padre_id),
      )
      .sort((a, b) =>
        a.tipo === b.tipo
          ? a.nombre.localeCompare(b.nombre, 'es')
          : a.tipo.localeCompare(b.tipo),
      );
    for (const raiz of raices) {
      result.push(raiz);
      const hijos = (porPadre[raiz.id] ?? []).sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es'),
      );
      result.push(...hijos);
    }
    return result;
  }, [filtradas]);

  async function handleDesactivar(c: CategoriaRead) {
    const ok = window.confirm(
      `¿Desactivar la categoría "${c.nombre}"?\n\nLos registros históricos que la usan seguirán intactos; solo deja de aparecer como opción al clasificar nuevos movimientos. Puedes reactivarla después.`,
    );
    if (!ok) return;
    setDesactivandoId(c.id);
    setErrorAccion(null);
    try {
      await desactivarCategoria(c.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorAccion(detail);
      } else {
        setErrorAccion('Error de red al desactivar');
      }
    } finally {
      setDesactivandoId(null);
    }
  }

  async function handleReactivar(c: CategoriaRead) {
    setDesactivandoId(c.id);
    setErrorAccion(null);
    try {
      await actualizarCategoria(c.id, { activa: true });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorAccion(detail);
      } else {
        setErrorAccion('Error de red al reactivar');
      }
    } finally {
      setDesactivandoId(null);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Catálogo financiero"
        title="Categorías"
        description="Etiquetas reutilizables para clasificar movimientos financieros (ingresos varios, gastos, compensaciones). El color se usa en los gráficos del portal. Desactivar una categoría no elimina los registros que la usan: solo la oculta como opción al clasificar nuevos movimientos."
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nueva categoría
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value as TipoCategoria | 'todos')}
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por tipo"
        >
          <option value="todos">Todos los tipos</option>
          <option value="ingreso">Solo ingreso</option>
          <option value="gasto">Solo gasto</option>
        </select>
        <label className="flex items-center gap-2 text-text-secondary">
          <input
            type="checkbox"
            checked={soloActivas}
            onChange={(e) => setSoloActivas(e.target.checked)}
          />
          <span>Solo activas</span>
        </label>
        <span className="text-xs text-text-muted">
          {filtradas.length} de {items.length}
        </span>
      </div>

      {errorAccion && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {errorAccion}{' '}
          <button
            type="button"
            onClick={() => setErrorAccion(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && filtradas.length === 0 && (
        <p className="text-sm text-text-muted">
          {items.length === 0
            ? 'No hay categorías cargadas. Usa "+ Nueva categoría" para crear la primera.'
            : 'No hay categorías que coincidan con el filtro.'}
        </p>
      )}

      {!loading && !error && ordenadas.length > 0 && (
        <Table>
          <THead>
            <Th className="w-12"></Th>
            <Th>Nombre</Th>
            <Th>Tipo</Th>
            <Th>Estado</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {ordenadas.map((c) => {
              const esHija = c.categoria_padre_id !== null;
              return (
              <Tr key={c.id} data-testid="fila-categoria">
                <Td>
                  <div
                    className="w-6 h-6 rounded-sm border-[0.5px] border-white/15"
                    style={{ backgroundColor: c.color }}
                    title={c.color}
                  />
                </Td>
                <Td tone="primary">
                  {esHija && (
                    <span className="text-text-muted mr-2" aria-hidden>
                      └─
                    </span>
                  )}
                  {c.nombre}
                </Td>
                <Td>{ETIQUETA_TIPO[c.tipo]}</Td>
                <Td tone={c.activa ? 'success' : 'muted'}>
                  {c.activa ? 'Activa' : 'Desactivada'}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <ActionButton
                      variant="edit"
                      onClick={() => setEditando(c)}
                      disabled={desactivandoId === c.id}
                    >
                      Editar
                    </ActionButton>
                    {c.activa ? (
                      <ActionButton
                        variant="danger"
                        onClick={() => handleDesactivar(c)}
                        disabled={desactivandoId === c.id}
                      >
                        {desactivandoId === c.id ? 'Desactivando…' : 'Desactivar'}
                      </ActionButton>
                    ) : (
                      <ActionButton
                        variant="success"
                        onClick={() => handleReactivar(c)}
                        disabled={desactivandoId === c.id}
                      >
                        {desactivandoId === c.id ? 'Reactivando…' : 'Reactivar'}
                      </ActionButton>
                    )}
                  </div>
                </Td>
              </Tr>
              );
            })}
          </TBody>
        </Table>
      )}

      {mostrarCrear && (
        <ModalForm
          inicial={null}
          candidatosPadre={items}
          onCerrar={() => setMostrarCrear(false)}
          onGuardada={() => {
            setMostrarCrear(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalForm
          inicial={editando}
          candidatosPadre={items}
          onCerrar={() => setEditando(null)}
          onGuardada={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
